/* jshint esversion: 6 */
class RepositoryEntryValidation {
    /**
     * Constructs a new RepositoryEntryValidation instance.
     * @param {Object} entry - The entry to be validated.
     * @param {Object} $injector - AngularJS injector service.
     */
    constructor(entry, $injector) {
        this.entry = entry;
        this.$injector = $injector;
        this._validators = [];
        this._ValidatorService = null;
        this._TaskDataService = null;
        this._TaskSelectionService = null;
        this._fetchContext = null;
        this._timeout = null;
        this._translate = null;
        this._parents = null;
        this._validationInProcess = {};
    }

    /**
     * Retrieves the ValidatorService instance.
     * @returns {Object} - The ValidatorService instance.
     */
    get ValidatorService() {
        if (!this._ValidatorService) {
            this._ValidatorService = this.$injector.get('ValidatorService');
        }
        return this._ValidatorService;
    }

    /**
     * Retrieves the TaskDataService instance.
     * @returns {Object} - The TaskDataService instance.
     */
    get TaskDataService() {
        if (!this._TaskDataService) {
            this._TaskDataService = this.$injector.get('TaskDataService');
        }
        return this._TaskDataService;
    }

    /**
     * Retrieves the TaskSelectionService instance.
     * @returns {Object} - The TaskSelectionService instance.
     */
    get TaskSelectionService() {
        if (!this._TaskSelectionService) {
            this._TaskSelectionService = this.$injector.get('TaskSelectionService');
        }
        return this._TaskSelectionService;
    }

    /**
     * Retrieves the fetch context.
     * @returns {Object} - The fetch context.
     */
    get fetchContext() {
        if (!this._fetchContext) {
            this._fetchContext = this.TaskDataService.fetchContext();
        }
        return this._fetchContext;
    }

    /**
     * Retrieves the $timeout service.
     * @returns {Object} - The $timeout service.
     */
    get $timeout() {
        if (!this._timeout) {
            this._timeout = this.$injector.get('$timeout');
        }
        return this._timeout;
    }

    /**
     * Retrieves the $translate service.
     * @returns {Object} - The $translate service.
     */
    get $translate() {
        if (!this._translate) {
            this._translate = this.$injector.get('$translate');
        }
        return this._translate;
    }

    /**
     * Calls the callbacks with the given result.
     * @param {Object} callbacks - The callbacks to be called.
     * @param {Object} result - The result to be passed to the callbacks.
     */
    _callCallBacks(callbacks, result) {
        for (let key in callbacks) {
            let callback = callbacks[key];
            this.ValidatorService.callOnErrorCallBack(callback, result);
        }
    }

    /**
     * Retrieves the parents of the entry.
     * @returns {Array} - The parents of the entry.
     */
    getParents() {
        if (!this._parents) {
            this._parents = this.fetchContext.getParentsEntryByUUID(this.getUUID());
        }
        return this._parents;
    }

    /**
     * Removes the validation in process for the given validator.
     * @param {Object} validator - The validator to remove the validation in process for.
     */
    _removeValidationInProcess(validator) {
        this.$timeout(() => {
            delete this._validationInProcess[validator.getValidatorName()];
        }, 0);
    }

    /**
     * Validates the entry.
     * @param {Object} from - The source of the validation request.
     * @returns {boolean} - True if validation passes, false otherwise.
     */
    validate(from) {
        let result = {
            'error': false,
            'messages': []
        };

        let taskUUID = this.TaskSelectionService.getTaskUUID();
        let task = this.fetchContext.read(taskUUID);
        let exists_error = false;
        let thisObj = this;

        if (task.getSheets().length === 0 && !thisObj.isFlaggedAsDeleted()) return false;

        if (!this.ValidatorService.getCurrentTask()) {
            this.ValidatorService.destroyValidationServiceInstance();
            return false;
        }

        this._parents = undefined;

        for (let key in this._validators) {
            let validator = this._validators[key];

            if (validator) {
                if (thisObj.isFlaggedAsDeleted()) {
                    this.ValidatorService.removeValidatorMessages(thisObj.getUUID(), validator.getValidatorName());
                    continue;
                }
                if (exists_error) {
                    if (!(angular.isFunction(validator.forcedCheck) && validator.forcedCheck())) {
                        this.ValidatorService.removeValidatorMessages(thisObj.getUUID(), validator.getValidatorName());
                        break;
                    }
                }

                if (this._validationInProcess[validator.getValidatorName()] && this._validationInProcess[validator.getValidatorName()] === true) {
                    continue;
                }
                this._validationInProcess[validator.getValidatorName()] = true;

                let validate = validator.validate(from);

                if (Array.isArray(validate)) {
                    for (let key in validate) {
                        let validateObject = validate[key];
                        this.ValidatorService.removeValidatorMessages(validateObject.modelUUID, validateObject.validator);
                        thisObj.data.validation_message = null;
                        this._callCallBacks(validateObject.callbacks, '');

                        if (validateObject && validateObject.error) {
                            if (!validateObject.onlyBanner) {
                                result.error = true;
                                result.messages.push(validateObject);
                            }

                            this.ValidatorService.addValidatorMessages(validateObject);
                            if (validateObject['data']) delete validateObject['data'];
                            thisObj.data.validation_message = validateObject;

                            exists_error = validateObject.status === 'DANGER';
                        }

                        this._callCallBacks(validateObject.callbacks, result);
                    }
                } else {
                    this.ValidatorService.removeValidatorMessages(validate.modelUUID, validate.validator);
                    thisObj.data.validation_message = null;
                    this._callCallBacks(validate.callbacks, '');

                    if (validate && validate.error) {
                        if (!validate.onlyBanner) {
                            result.error = true;
                            result.messages.push(validate);
                        }

                        this.ValidatorService.addValidatorMessages(validate);
                        if (validate['data']) delete validate['data'];
                        thisObj.data.validation_message = validate;

                        exists_error = validate.status === 'DANGER';
                    }

                    this._callCallBacks(validate.callbacks, result);
                }
                this._removeValidationInProcess(validator);
            }
        }

        this.ValidatorService.callOnErrorCallBack('validation_banner_directive');

        this._parents = undefined;
    }

    /**
     * Retrieves the question headline.
     * @returns {string} - The question headline.
     */
    getQuestionHeadline() {
        let parents = this.getParents();
        let result = '';
        if (Array.isArray(parents)) {
            let questionPart;
            let sheet = _.find(parents, function (item) {
                return item.getType() === 'sheet';
            });

            if (this.getType() === 'questionPart') {
                questionPart = this;
            } else {
                questionPart = _.find(parents, function (item) {
                    return item.getType() === 'questionPart';
                });
            }
            if (sheet) {
                if (angular.isFunction(sheet.isJournal) && sheet.isJournal()) {
                    result = sheet.getTitle() ? sheet.getTitle() : this.$translate.instant('PREFIX.JOURNAL');
                } else if (angular.isFunction(sheet.isPreRelease) && sheet.isPreRelease()) {
                    result = sheet.getTitle() ? sheet.getTitle() : this.$translate.instant('PREFIX.PRE-RELEASE');
                } else if (questionPart) {
                    result = questionPart.getTitle() ? (questionPart.getTitle()) : (sheet.getTitle() ? (sheet.getTitle() + '  ' + sheet.getShortTitle() + questionPart.getShortTitle()) : (this.$translate.instant("PREFIX.QUESTION-PART") + ' ' + (sheet.getShortTitle() + questionPart.getShortTitle())));
                } else {
                    result = sheet.getTitle() ? (sheet.getTitle() + '  ' + sheet.getShortTitle()) : (this.$translate.instant("PREFIX.QUESTION-PART") + ' ' + (sheet.getShortTitle()));
                }
            }
        }

        return result;
    }

    /**
     * Unbinds the validation for the entry.
     */
    unbindValidate() {
        this.ValidatorService.removeAllValidatorMessagesByUUID(this.getUUID());
    }

    /**
     * Sets the validators for the entry.
     * @param {Array} validators - The validators to be set.
     * @returns {boolean} - False if not in CMS, true otherwise.
     */
    validators(validators) {
        if (!isCMS) return false;
        let _this = this;
        for (let key in validators) {
            let validatorFactoryName = validators[key];
            if (this.$injector.has(validatorFactoryName)) {
                let validationClass = this.$injector.get(validatorFactoryName);
                this._validators.push(new validationClass(this));
            }
        }

        this.$timeout(() => {
            if (this.ValidatorService.getCurrentTask()) {
                _this.validate(null);
            }
        }, 1500);
    }
}

export default RepositoryEntryValidation;